import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { LimitReachedModal } from 'components/limits/LimitReachedModal';
import { useUser } from 'services/User';
import { getLimitAudience, getRateLimitKind, getRateLimitMessage, type RateLimitKind } from 'utils/rateLimit';

interface LimitPromptContextType {
    /**
     * `true` means the error was recognised as a rate-limit rejection and the prompt was opened,
     * so the caller must suppress its own toast and stop. `false` leaves the error to the caller.
     */
    showLimitPromptForError: (error: unknown) => boolean;
}

// The default declines every error instead of throwing: consumers rendered outside the provider
// (existing component specs, Storybook, embed surfaces) must degrade to their current toast.
const LimitPromptContext = createContext<LimitPromptContextType>({ showLimitPromptForError: () => false });

export const LimitPromptProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useUser();
    const audience = getLimitAudience(user);
    const [limit, setLimit] = useState<{ kind: RateLimitKind; message: string | null } | null>(null);

    const showLimitPromptForError = useCallback((error: unknown) => {
        const kind = getRateLimitKind(error);

        if (!kind) {
            return false;
        }

        // SWR is configured with `errorRetryCount: 2` (`src/services/swr.tsx`), so one throttled
        // request can surface up to three error objects. Keeping the first classification while
        // the prompt is open stops a retry from rewriting the copy under the user — same guard
        // rationale as `src/hooks/useBlogErrorToast.ts`.
        setLimit((current) => current ?? { kind, message: getRateLimitMessage(error) });

        return true;
    }, []);

    const value = useMemo<LimitPromptContextType>(() => ({ showLimitPromptForError }), [showLimitPromptForError]);

    return (
        <LimitPromptContext.Provider value={value}>
            {children}
            <LimitReachedModal
                open={limit !== null}
                kind={limit?.kind ?? 'daily'}
                message={limit?.message}
                audience={audience}
                onOpenChange={(open) => {
                    // Reset to `null` rather than keeping the last rejection so the next failed
                    // attempt re-opens the prompt. Deliberately no localStorage snooze.
                    if (!open) {
                        setLimit(null);
                    }
                }}
            />
        </LimitPromptContext.Provider>
    );
};

export const useLimitPrompt = () => useContext(LimitPromptContext);
